import { useSyncExternalStore } from 'react';
import { ApiError, request, setConnectionHandler } from './api.ts';
import type { Catalog, Company, Register } from './types.ts';

/**
 * Selling without a connection. While the server cannot be reached, the till keeps a copy
 * of the menu and the register's shift on this device, records cash and card sales here,
 * and sends them one by one when the connection is back. Each sale carries its own id, so
 * sending it twice never records it twice.
 */

export interface OfflineLine {
  product: string;
  name: string;
  qty: number;
  unit_price: number;
  modifiers: { id: string; name: string; price_delta: number }[];
  /** The price time (happy hour) that gave it its price. */
  price_rule?: string | null;
}
export interface OfflineSale {
  client_id: string;
  session: string;
  name: string;
  channel: 'counter' | 'takeaway';
  paid_at: string;
  lines: OfflineLine[];
  payments: { method: 'cash' | 'card'; amount: number; received?: number }[];
  total: number;
  by: string;
  /** Set when the server refused it: a person has to look at it. */
  error?: string;
  /** Receipt number once it reached the server. */
  receipt_no?: number;
  sent_at?: string;
}
export interface Snapshot {
  saved_at: string;
  catalog: Catalog;
  register: Register;
  company: Company;
  /** What the tax number is called on the printed proof: NIPT, EIN… */
  tax_id_label?: string;
}

const QUEUE_KEY = 'erp.offline.sales';
const SNAP_KEY = 'erp.offline.snapshot';
/** Sent sales stay visible on the device for a day, then go. */
const KEEP_SENT_MS = 24 * 3_600_000;

const read = <T>(key: string, fallback: T): T => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null') ?? fallback;
  } catch {
    return fallback;
  }
};
const write = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the sale stays in memory until the page closes.
  }
};

// ---- tiny store -----------------------------------------------------------

interface State {
  online: boolean;
  syncing: boolean;
  queue: OfflineSale[];
}
let state: State = { online: typeof navigator === 'undefined' ? true : navigator.onLine, syncing: false, queue: read<OfflineSale[]>(QUEUE_KEY, []) };
const listeners = new Set<() => void>();
const set = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  if (patch.queue) write(QUEUE_KEY, patch.queue);
  for (const l of listeners) l();
};
const subscribeStore = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useOffline() {
  const s = useSyncExternalStore(subscribeStore, () => state);
  return {
    online: s.online,
    syncing: s.syncing,
    queue: s.queue,
    waiting: s.queue.filter((x) => !x.receipt_no && !x.error).length,
    failed: s.queue.filter((x) => x.error && !x.receipt_no).length,
  };
}

/** Called by the API layer: every answer means the server is there; a network error means it is not. */
export function reportConnection(online: boolean) {
  if (online === state.online) return;
  set({ online });
  if (online) void syncNow();
  else startProbing();
}

let probe: ReturnType<typeof setInterval> | null = null;
function startProbing() {
  if (probe) return;
  probe = setInterval(async () => {
    try {
      const res = await fetch('/api/health', { cache: 'no-store' });
      if (res.ok) {
        clearInterval(probe!);
        probe = null;
        reportConnection(true);
      }
    } catch {
      // still down
    }
  }, 5000);
}

setConnectionHandler(reportConnection);
if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => reportConnection(false));
  window.addEventListener('online', () => startProbing());
}

// ---- what the till needs offline ------------------------------------------

export const snapshot = {
  get: (): Snapshot | null => read<Snapshot | null>(SNAP_KEY, null),
  /** Keeps the menu and this register's open shift on the device, while online. */
  async refresh(registerId: string | null): Promise<Snapshot | null> {
    try {
      const [registers, company] = await Promise.all([
        request<Register[]>('POST', '/api/actions/pos.registers', {}),
        request<Company>('POST', '/api/actions/company.get', {}),
      ]);
      const register = registers.find((r) => r.id === registerId) ?? (registers.length === 1 ? registers[0] : undefined);
      if (!register) return snapshot.get();
      // The menu of the register's own location: its prices, what it sells.
      const catalog = await request<Catalog>('POST', '/api/actions/products.catalog', { location: register.location ?? null });
      const rules = await request<{ taxId: { label: string } }>('POST', '/api/actions/company.country_rules', { country: company.country }).catch(() => null);
      const snap: Snapshot = { saved_at: new Date().toISOString(), catalog, register, company, tax_id_label: rules?.taxId.label };
      write(SNAP_KEY, snap);
      return snap;
    } catch {
      return snapshot.get();
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(SNAP_KEY);
    } catch {
      // ignore
    }
  },
};

// ---- the queue ------------------------------------------------------------

export function addSale(sale: Omit<OfflineSale, 'client_id' | 'paid_at'>): OfflineSale {
  const full: OfflineSale = { ...sale, client_id: crypto.randomUUID(), paid_at: new Date().toISOString() };
  set({ queue: [...state.queue, full] });
  if (state.online) void syncNow();
  return full;
}

/** A sale the server refused can be dropped by a manager once it is entered by hand. */
export function dropSale(clientId: string) {
  set({ queue: state.queue.filter((x) => x.client_id !== clientId) });
}

export function retrySale(clientId: string) {
  set({ queue: state.queue.map((x) => (x.client_id === clientId ? { ...x, error: undefined } : x)) });
  void syncNow();
}

let running: Promise<void> | null = null;
/** Sends what is waiting, oldest first. Stops at the first network error and tries again later. */
export function syncNow(): Promise<void> {
  running ??= (async () => {
    set({ syncing: true });
    try {
      const cutoff = Date.now() - KEEP_SENT_MS;
      set({ queue: state.queue.filter((x) => !x.sent_at || Date.parse(x.sent_at) > cutoff) });
      for (const sale of state.queue.filter((x) => !x.receipt_no && !x.error)) {
        try {
          const { total: _t, by: _b, error: _e, receipt_no: _r, sent_at: _s, ...body } = sale;
          const r = await request<{ receipt_no: number }>('POST', '/api/actions/restaurant.offline_sale', {
            ...body,
            lines: sale.lines.map(({ name: _n, ...l }) => l),
          });
          patch(sale.client_id, { receipt_no: r.receipt_no, sent_at: new Date().toISOString() });
        } catch (err) {
          if (err instanceof ApiError && err.code === 'offline') return;
          // Signed out: try again after the next sign-in.
          if (err instanceof ApiError && err.status === 401) return;
          patch(sale.client_id, { error: err instanceof Error ? err.message : String(err) });
        }
      }
    } finally {
      set({ syncing: false });
      running = null;
    }
  })();
  return running;
}

function patch(clientId: string, p: Partial<OfflineSale>) {
  set({ queue: state.queue.map((x) => (x.client_id === clientId ? { ...x, ...p } : x)) });
}
